import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, FileText, Loader2, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { inrFull } from "@/lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  submitted: "border-brass/50 bg-brass/10 text-brass-deep",
  under_process: "border-sage/60 bg-sage/20 text-concordia",
};

export default function AccountPage() {
  useSEO({ title: "My Account — Loan Salaah", description: "Track your Loan Salaah loan and credit card applications." });
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [applications, setApplications] = useState(null);

  useEffect(() => {
    if (user === false) navigate("/login", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    axios
      .get(`${API}/account/applications`, { withCredentials: true })
      .then(({ data }) => setApplications(data.applications))
      .catch(() => setApplications([]));
  }, [user]);

  if (!user) {
    return (
      <div data-testid="account-loading" className="flex min-h-[60vh] items-center justify-center pt-24">
        <Loader2 className="h-8 w-8 animate-spin text-concordia" />
      </div>
    );
  }

  const displayUser = user || location.state?.user;

  return (
    <div data-testid="account-page" className="pb-24 pt-32">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="flex flex-col gap-6 rounded-3xl border border-sand bg-white p-6 shadow-[0_30px_80px_rgba(20,40,75,0.08)] sm:flex-row sm:items-center sm:justify-between sm:p-8">
            <div className="flex items-center gap-4">
              {displayUser.picture ? (
                <img src={displayUser.picture} alt="" className="h-14 w-14 rounded-full border border-sand object-cover" data-testid="account-avatar" />
              ) : (
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-concordia font-serif text-xl font-semibold text-ivory" data-testid="account-avatar">
                  {displayUser.name?.charAt(0)?.toUpperCase() || "L"}
                </span>
              )}
              <div>
                <p className="font-mono text-[0.62rem] uppercase tracking-[0.3em] text-brass">My account</p>
                <h1 className="mt-1 font-serif text-2xl font-semibold tracking-tight text-concordia" data-testid="account-name">
                  Namaste, {displayUser.name?.split(" ")[0] || "there"}.
                </h1>
                <p className="text-xs text-slate" data-testid="account-email">{displayUser.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/apply" data-testid="account-apply-button"
                className="inline-flex items-center gap-1.5 rounded-full bg-concordia px-5 py-2.5 text-xs font-semibold text-ivory transition-colors hover:bg-concordia-deep">
                New application <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
              <button onClick={async () => { await logout(); navigate("/"); }} data-testid="account-signout-button"
                className="inline-flex items-center gap-1.5 rounded-full border border-sand px-5 py-2.5 text-xs font-semibold text-slate transition-colors hover:border-concordia/40 hover:text-concordia">
                <LogOut className="h-3.5 w-3.5" /> Sign out
              </button>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <h2 className="mt-12 font-serif text-3xl font-semibold tracking-tight text-concordia">Your applications</h2>
          {applications === null ? (
            <div className="mt-8 flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-concordia" /></div>
          ) : applications.length === 0 ? (
            <div data-testid="applications-empty" className="mt-6 rounded-3xl border border-dashed border-sand bg-white/60 p-12 text-center">
              <FileText className="mx-auto h-8 w-8 text-slate/50" />
              <p className="mt-4 text-sm font-semibold text-charcoal">No applications yet</p>
              <p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-slate">
                Enquiries you make with this email appear here automatically, with their live status.
              </p>
              <Link to="/apply" data-testid="applications-empty-apply-link"
                className="mt-6 inline-block rounded-full bg-concordia px-8 py-3 text-sm font-semibold text-ivory transition-colors hover:bg-concordia-deep">
                Start an application
              </Link>
            </div>
          ) : (
            <ul className="mt-6 space-y-4" data-testid="applications-list">
              {applications.map((a) => (
                <li key={a.enquiry_id} data-testid={`application-card-${a.enquiry_id}`}
                  className="rounded-2xl border border-sand bg-white p-5 transition-shadow hover:shadow-[0_16px_40px_rgba(20,40,75,0.08)] sm:p-6">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-serif text-lg font-semibold text-concordia">{a.loan_type}</p>
                      <p className="mt-0.5 text-sm text-charcoal">
                        {inrFull(a.amount)} · {a.city}
                      </p>
                      <p className="mt-1 font-mono text-[0.65rem] uppercase tracking-wider text-slate/70">
                        {a.enquiry_id} · {new Date(a.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                      </p>
                    </div>
                    <span data-testid={`application-status-${a.enquiry_id}`}
                      className={`rounded-full border px-3.5 py-1.5 text-[0.65rem] font-semibold uppercase tracking-[0.15em] ${STATUS_STYLE[a.status.code] || STATUS_STYLE.submitted}`}>
                      {a.status.label}
                    </span>
                  </div>
                  <p className="mt-3 border-t border-sand/70 pt-3 text-xs leading-relaxed text-slate">{a.status.note}</p>
                </li>
              ))}
            </ul>
          )}
        </Reveal>
      </div>
    </div>
  );
}
